import React, { useState, useEffect, useRef, useCallback } from "react";
import "./styles/kiosk.css";
import atmantanLogo from "./assets/atmantan_logo_reverse.webp";
import {
  verifyGuest,
  verifyKiosk,
  fetchItinerary,
  fetchMyFeedback,
  fetchAvailableActivities,
  enrollInActivity,
  submitTreatmentFeedback,
  submitGeneralFeedback,
  getStoredToken,
  storeSession,
  clearSession,
} from "./services/guestFeedbackApi";

const IDLE_MS = 120000;
const AUTH_IDLE_MS = 60000;
const PIN_LENGTH = 6;

const MAP_ZONES = [
  { zone: "Wellness Pavilion", note: "Therapies & doctor consults", cabins: ["Vaari", "Nira", "Dyaus"], x: 30, y: 30 },
  { zone: "Kriya Pavilion", note: "Yogic kriyas & pranayama", cabins: [], x: 62, y: 40 },
  { zone: "Yoga Studio", note: "Morning flow & restorative", cabins: [], x: 46, y: 22 },
  { zone: "Dining Pavilion", note: "Wellness cuisine", cabins: [], x: 24, y: 58 },
  { zone: "Buddha Grove", note: "Meditation & reflection", cabins: [], x: 70, y: 64 },
  { zone: "Mulshi Lake Deck", note: "Sunrise & sound healing", cabins: [], x: 50, y: 82 },
];

function zoneForLocation(loc) {
  const z = MAP_ZONES.find((zone) => zone.zone === loc || zone.cabins.includes(loc));
  return z ? z.zone : loc;
}

function cap(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function addDays(iso, days) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function getDayLabel(dateIso, profile, todayIso) {
  if (profile?.checkIn) {
    const dayNum = Math.max(1, Math.floor((Date.parse(dateIso) - Date.parse(profile.checkIn)) / 86400000) + 1);
    return `Day ${dayNum}`;
  }
  return dateIso === todayIso ? "Today" : new Date(dateIso + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function formatScheduleDate(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function getStayBounds(profile, todayIso) {
  const checkIn = profile?.checkIn || todayIso;
  let checkOut = profile?.checkOut || null;
  const nightsMatch = String(profile?.nights ?? "").match(/\d+/);
  const nights = nightsMatch ? Number(nightsMatch[0]) : NaN;

  if (!checkOut && profile?.checkIn && Number.isFinite(nights) && nights > 0) {
    checkOut = addDays(profile.checkIn, nights);
  }
  if (!checkOut && profile?.checkIn) {
    checkOut = addDays(profile.checkIn, 13);
  }
  if (!checkOut) {
    checkOut = addDays(todayIso, 7);
  }

  return { stayStart: checkIn, stayEnd: checkOut };
}

function itemMeta(it) {
  return [cap(it.kind), it.location, it.instructor].filter(Boolean).join(" · ");
}

function mapFeedbackToState(items, feedback) {
  const keyToId = {};
  for (const it of items) {
    if (it.bookingKey) keyToId[it.bookingKey] = it.id;
  }
  const treatmentRatings = {};
  for (const fb of feedback?.treatment || []) {
    const id = keyToId[fb.bookingKey];
    if (id) treatmentRatings[id] = { rating: fb.rating, comment: fb.comment || "" };
  }
  const general = feedback?.general;
  return {
    treatmentRatings,
    generalRating: general?.rating || 0,
    generalText: general?.comment || "",
    generalDone: Boolean(general),
  };
}

function Logo({ light, className }) {
  return <img src={atmantanLogo} alt="ātmantan" className={"logo" + (light ? " light" : "") + (className ? ` ${className}` : "")} />;
}

function Lotus({ size = 22, filled = false, tone }) {
  const stroke = tone || "var(--gold-deep)";
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 20c-4 0-7-2.4-7-5.6 0 0 2.2.4 3.6 1.7C7.2 13.4 8 10.6 12 7c4 3.6 4.8 6.4 3.4 9.1 1.4-1.3 3.6-1.7 3.6-1.7C19 17.6 16 20 12 20Z" fill={filled ? "var(--gold)" : "none"} stroke={stroke} strokeWidth="1.1" strokeLinejoin="round" />
      <path d="M12 20c-2.4 0-4.3-2.2-4.3-5.2 0 0 2.4 1 4.3 4 1.9-3 4.3-4 4.3-4 0 3-1.9 5.2-4.3 5.2Z" fill={filled ? "var(--gold-deep)" : "none"} stroke={stroke} strokeWidth="0.9" strokeLinejoin="round" opacity={filled ? 0.35 : 1} />
    </svg>
  );
}

function NavIcon({ name, size = 24 }) {
  const svgProps = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  switch (name) {
    case "today":
      return (
        <svg {...svgProps}>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M8 3v4M16 3v4M3 11h18" />
        </svg>
      );
    case "activities":
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="5" r="2" />
          <path d="M12 7v2" />
          <path d="M7 11c2-1.2 3.5-1.2 5 0s3 1.2 5 0" />
          <path d="M8 20l4-8 4 8" />
          <path d="M9.5 20h5" />
        </svg>
      );
    case "map":
      return (
        <svg {...svgProps}>
          <path d="M12 21s7-4.35 7-11a7 7 0 1 0-14 0c0 6.65 7 11 7 11z" />
          <circle cx="12" cy="10" r="2.5" />
        </svg>
      );
    case "reflect":
      return <Lotus size={size} tone="currentColor" />;
    default:
      return null;
  }
}

function Pill({ children }) {
  return <span className="k-pill">{children}</span>;
}

function Toast({ text, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2800);
    return () => clearTimeout(t);
  }, [text, onDone]);
  return <div className="k-toast" role="status">{text}</div>;
}

function DateBlock({ iso, align }) {
  const d = new Date(iso + "T00:00:00");
  return (
    <div className={"k-dateblock " + (align || "")}>
      <span className="k-db-year">{d.getFullYear()}</span>
      <span className="k-db-day">{d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }).toUpperCase()}</span>
    </div>
  );
}

function Rating({ value, onChange, big }) {
  const labels = ["", "Could be gentler", "Fair", "Good", "Wonderful", "Transformative"];
  return (
    <div className="k-rating-wrap">
      <div className={"k-rating" + (big ? " big" : "")}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" className={"k-pip" + (value >= n ? " on" : "")} onClick={() => onChange(n)} aria-label={`${n} of 5`}>
            <Lotus size={big ? 46 : 38} filled={value >= n} />
          </button>
        ))}
      </div>
      <p className="k-rating-label">{value ? labels[value] : "Tap to rate"}</p>
    </div>
  );
}

export default function GuestKioskApp() {
  const [mode, setMode] = useState("attract");
  const [section, setSection] = useState("today");
  const [profile, setProfile] = useState(null);
  const [itinerary, setItinerary] = useState([]);
  const [scheduleDate, setScheduleDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [pickCandidates, setPickCandidates] = useState([]);
  const [pendingLastSix, setPendingLastSix] = useState("");
  const [pin, setPin] = useState("");
  const [authErr, setAuthErr] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [treatmentRatings, setTreatmentRatings] = useState({});
  const [activeTreatment, setActiveTreatment] = useState(null);
  const [generalRating, setGeneralRating] = useState(0);
  const [generalText, setGeneralText] = useState("");
  const [generalDone, setGeneralDone] = useState(false);
  const [toast, setToast] = useState(null);
  const idleRef = useRef();
  const authIdleRef = useRef();

  const today = new Date().toISOString().slice(0, 10);
  const { stayStart, stayEnd } = getStayBounds(profile, today);
  const dayLabel = getDayLabel(scheduleDate, profile, today);
  const canGoPrevDay = scheduleDate > stayStart;
  const canGoNextDay = scheduleDate < stayEnd;
  const viewingToday = scheduleDate === today;

  const eligible = itinerary.filter((i) => i.kind === "treatment" && i.feedbackEligible);
  const doneCount = Object.keys(treatmentRatings).length;
  const nextUp = itinerary.find((i) => !i.completed) || itinerary[0];

  const loadItinerary = useCallback(async (date = scheduleDate) => {
    setScheduleLoading(true);
    try {
      const data = await fetchItinerary(date);
      setItinerary(data.items || []);
      return data.items || [];
    } catch (err) {
      console.error(err);
      setItinerary([]);
      return [];
    } finally {
      setScheduleLoading(false);
    }
  }, [scheduleDate]);

  const loadFeedback = useCallback(async (items) => {
    try {
      const feedback = await fetchMyFeedback();
      const mapped = mapFeedbackToState(items, feedback);
      setTreatmentRatings(mapped.treatmentRatings);
      setGeneralRating(mapped.generalRating);
      setGeneralText(mapped.generalText);
      setGeneralDone(mapped.generalDone);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const completeLogin = useCallback(async (result) => {
    storeSession(result.token, result.guest);
    setProfile(result.guest);
    setPickCandidates([]);
    setPendingLastSix("");
    setPin("");
    setAuthErr("");
    setScheduleDate(today);
    setMode("app");
    setSection("today");
  }, [today]);

  useEffect(() => {
    const token = getStoredToken();
    if (!token) return;
    try {
      const raw = sessionStorage.getItem("guest_feedback_guest");
      if (raw) setProfile(JSON.parse(raw));
    } catch (_) { /* ignore */ }
    setMode("app");
    setSection("today");
  }, []);

  useEffect(() => {
    if (mode !== "app" || !profile) return;
    loadItinerary(scheduleDate).then((items) => loadFeedback(items));
  }, [mode, profile, scheduleDate, loadItinerary, loadFeedback]);

  function cancelAuth() {
    clearTimeout(authIdleRef.current);
    setMode("attract");
    setPin("");
    setAuthErr("");
    setPickCandidates([]);
    setPendingLastSix("");
  }

  function resetAuthIdle() {
    if (mode !== "verify" && mode !== "choose") return;
    clearTimeout(authIdleRef.current);
    authIdleRef.current = setTimeout(cancelAuth, AUTH_IDLE_MS);
  }

  useEffect(() => {
    resetAuthIdle();
    return () => clearTimeout(authIdleRef.current);
  }, [mode, pin]); // eslint-disable-line react-hooks/exhaustive-deps

  function finishSession() {
    clearTimeout(authIdleRef.current);
    clearSession();
    setMode("attract");
    setSection("today");
    setProfile(null);
    setItinerary([]);
    setScheduleDate(today);
    setPickCandidates([]);
    setPendingLastSix("");
    setPin("");
    setAuthErr("");
    setTreatmentRatings({});
    setActiveTreatment(null);
    setGeneralRating(0);
    setGeneralText("");
    setGeneralDone(false);
    setToast(null);
  }

  function bumpIdle() {
    if (mode === "attract") return;
    clearTimeout(idleRef.current);
    idleRef.current = setTimeout(finishSession, IDLE_MS);
  }

  useEffect(() => {
    bumpIdle();
    return () => clearTimeout(idleRef.current);
  }, [mode, section]); // eslint-disable-line react-hooks/exhaustive-deps

  function pressDigit(d) {
    setAuthErr("");
    resetAuthIdle();
    if (pin.length < PIN_LENGTH) setPin(pin + d);
  }

  function AuthLogoButton({ onClick, ariaLabel }) {
    return (
      <button type="button" className="k-screen-logo k-auth-logo-btn" onClick={onClick} aria-label={ariaLabel}>
        <Logo light />
      </button>
    );
  }

  useEffect(() => {
    if (mode !== "verify" || pin.length !== PIN_LENGTH || verifying) return;
    const t = setTimeout(async () => {
      setVerifying(true);
      setAuthErr("");
      const code = pin;
      try {
        const result = await verifyKiosk({ lastSix: code });
        if (result.token) {
          await completeLogin(result);
        } else if (result.needsDisambiguation) {
          setPickCandidates(result.guests || []);
          setPendingLastSix(code);
          setPin("");
          setMode("choose");
        }
      } catch (err) {
        const msg = err.message || "";
        if (msg === "Failed to fetch" || msg.includes("NetworkError")) {
          setAuthErr("Cannot reach the server. Please ask reception.");
        } else {
          setAuthErr(msg || "That doesn't match our records.");
        }
        setPin("");
      } finally {
        setVerifying(false);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [pin, mode, verifying, completeLogin]);

  async function pickGuest(g) {
    if (verifying || !pendingLastSix) return;
    resetAuthIdle();
    setVerifying(true);
    setAuthErr("");
    try {
      const result = await verifyGuest({ guestId: g.guestId, lastSix: pendingLastSix });
      await completeLogin(result);
    } catch (err) {
      setAuthErr(err.message || "Could not verify. Please try again.");
      setPickCandidates([]);
      setPendingLastSix("");
      setMode("verify");
    } finally {
      setVerifying(false);
    }
  }

  function backToDialPad() {
    setPickCandidates([]);
    setPendingLastSix("");
    setPin("");
    setAuthErr("");
    setMode("verify");
  }

  function goSection(s) {
    setActiveTreatment(null);
    setSection(s);
  }

  function goPrevDay() {
    if (!canGoPrevDay) return;
    setScheduleDate(addDays(scheduleDate, -1));
  }

  function goNextDay() {
    if (!canGoNextDay) return;
    setScheduleDate(addDays(scheduleDate, 1));
  }

  const NAV = [
    { id: "today", label: "Today", icon: "today" },
    { id: "activities", label: "Activities", icon: "activities" },
    { id: "reflect", label: "Reflect", icon: "reflect" },
  ];

  function PageHead({ pill, title, sub }) {
    return (
      <header className="k-page-head">
        <Pill>{pill}</Pill>
        <h1 className="k-h1">{title}</h1>
        {sub && <p className="k-muted">{sub}</p>}
      </header>
    );
  }

  function DayNav({ loading = false, showPrevious = true }) {
    return (
      <div className="k-day-nav">
        {showPrevious && (
          <button type="button" className="k-day-nav-btn" onClick={goPrevDay} disabled={!canGoPrevDay || loading} aria-label="Previous day">
            ‹ Previous day
          </button>
        )}
        <button type="button" className="k-day-nav-btn primary" onClick={goNextDay} disabled={!canGoNextDay || loading} aria-label="Next day">
          Next day ›
        </button>
      </div>
    );
  }

  function Attract() {
    return (
      <div className="k-attract" onClick={() => { setMode("verify"); setPin(""); setAuthErr(""); }} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setMode("verify")}>
        <div className="k-attract-logo k-screen-logo"><Logo light /></div>
        <div className="k-attract-inner">
          <p className="k-namaste">Namaste</p>
          <p className="k-attract-lede">Your day, your reflections, and your way around the sanctuary.</p>
          <div className="k-touch">Touch to begin</div>
        </div>
        <div className="k-attract-foot">Atmantan Mulshi - In the land of Prana</div>
      </div>
    );
  }

  function Verify() {
    return (
      <div className="k-authwrap" onPointerDown={resetAuthIdle}>
        <AuthLogoButton onClick={cancelAuth} ariaLabel="Cancel and return to home" />
        <div className="k-auth center">
          <div className="k-auth-head">
            <h1 className="k-auth-title">View your personal itinerary</h1>
            <p className="k-auth-lede">Enter the last 6 digits of your registered mobile number.</p>
          </div>
          <div className="k-pin-dots">
            {Array.from({ length: PIN_LENGTH }, (_, i) => (
              <span key={i} className={"k-pin-dot" + (pin.length > i ? " filled" : "")} />
            ))}
          </div>
          {authErr && <p className="k-err">{authErr}</p>}
          <div className="k-numpad">
            {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
              <button key={d} type="button" className="k-numkey" onClick={() => pressDigit(d)} disabled={verifying}>{d}</button>
            ))}
            <span />
            <button type="button" className="k-numkey" onClick={() => pressDigit("0")} disabled={verifying}>0</button>
            <button type="button" className="k-numkey k-fn" onClick={() => { resetAuthIdle(); setPin((p) => p.slice(0, -1)); }} aria-label="Delete" disabled={verifying}>⌫</button>
          </div>
          <p className="k-fineprint">Not you? Please ask reception for assistance.</p>
        </div>
      </div>
    );
  }

  function ChooseGuest() {
    return (
      <div className="k-authwrap" onPointerDown={resetAuthIdle}>
        <AuthLogoButton onClick={backToDialPad} ariaLabel="Back" />
        <div className="k-auth">
          <div className="k-auth-head">
            <h1 className="k-auth-title">Who is this?</h1>
            <p className="k-auth-lede">We found more than one guest with this number. Please select your name.</p>
          </div>
          {authErr && <p className="k-err">{authErr}</p>}
          <div className="k-guest-pick">
            {pickCandidates.map((g) => (
              <button key={g.guestId} type="button" className="k-guest-row" onClick={() => pickGuest(g)} disabled={verifying}>
                <span className="k-guest-row-init">{g.guestName.split(" ").map((n) => n[0]).join("").slice(0, 2)}</span>
                <span className="k-guest-row-name">{g.guestName}</span>
                <span className="k-chev">›</span>
              </button>
            ))}
          </div>
          <p className="k-fineprint">Not you? Please ask reception for assistance.</p>
        </div>
      </div>
    );
  }

  function Today() {
    if (!profile) return null;
    const scheduleHeading = viewingToday
      ? "Your schedule today"
      : `Your schedule · ${new Date(scheduleDate + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short" })}`;
    return (
      <div className="k-page">
        <header className="k-page-head k-page-head-today">
          <div>
            <p className="k-eyebrow">{dayLabel} · {profile.program}</p>
            <h1 className="k-h1">Namaste, {profile.firstName}</h1>
          </div>
          <DayNav loading={scheduleLoading} showPrevious={false} />
        </header>
        <div className="k-today-grid">
          <section>
            <p className="k-section-label">{scheduleHeading}</p>
            {scheduleLoading && <p className="k-muted">Loading schedule…</p>}
            {!scheduleLoading && itinerary.length === 0 && (
              <p className="k-muted">Nothing scheduled for this day.</p>
            )}
            <div className="k-timeline">
              {itinerary.map((it, idx) => {
                const rated = treatmentRatings[it.id];
                const isNext = nextUp && it.id === nextUp.id;
                return (
                  <div key={it.id} className={"k-tl" + (it.completed ? " done" : "") + (isNext ? " next" : "")}>
                    <div className="k-tl-rail">
                      <span className={"k-tl-node" + (it.completed ? " done" : "")}>
                        {it.completed ? <Lotus size={18} filled /> : <span className="k-tl-dot" />}
                      </span>
                      {idx < itinerary.length - 1 && <span className="k-tl-thread" />}
                    </div>
                    <div className="k-tl-body">
                      <div className="k-tl-time">
                        {it.start}<span className="k-tl-end"> – {it.end}</span>
                        {isNext && <span className="k-next-tag">Next</span>}
                      </div>
                      <p className="k-tl-title">{it.title}</p>
                      <p className="k-muted">{itemMeta(it)}</p>
                      {it.feedbackEligible && (
                        <button type="button" className={"k-inline-fb" + (rated ? " done" : "")} onClick={() => { setActiveTreatment(it); setSection("reflect"); }}>
                          {rated ? `Reflected · ${rated.rating}/5` : "Share feedback ›"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
          <aside className="k-side">
            {nextUp && (
              <div className="k-panelcard">
                <p className="k-eyebrow onp">{viewingToday ? "Next on your journey" : "First up this day"}</p>
                <p className="k-next-time">{nextUp.start}</p>
                <p className="k-next-title">{nextUp.title}</p>
                <p className="k-onp-soft">{nextUp.location} · {zoneForLocation(nextUp.location)}</p>
                <button type="button" className="k-linkbtn light" onClick={() => goSection("map")}>Find your way ›</button>
              </div>
            )}
            {viewingToday && (
            <div className="k-card k-nudge" onClick={() => goSection("reflect")} role="button" tabIndex={0}>
              <p className="k-eyebrow gold">Reflect</p>
              <p className="k-nudge-title">Share how today's sessions felt</p>
              <span className="k-linkbtn">Begin ›</span>
            </div>
            )}
            <div className="k-card k-stay">
              <div className="k-stayrow">
                <DateBlock iso={profile.checkIn} />
                <span className="k-nights">{profile.nights} Nights</span>
                <DateBlock iso={profile.checkOut} align="right" />
              </div>
            </div>
          </aside>
        </div>
      </div>
    );
  }

  function Activities() {
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [selected, setSelected] = useState(null);
    const [joining, setJoining] = useState(false);
    const [joinErr, setJoinErr] = useState("");

    const loadActivities = useCallback(() => {
      setLoading(true);
      return fetchAvailableActivities(scheduleDate)
        .then((data) => {
          setActivities(data.activities || []);
          setError("");
        })
        .catch((e) => {
          setError(e.message || "Could not load activities");
          setActivities([]);
        })
        .finally(() => setLoading(false));
    }, [scheduleDate]);

    useEffect(() => {
      loadActivities();
    }, [loadActivities]);

    async function handleJoin(activity) {
      setJoining(true);
      setJoinErr("");
      try {
        const result = await enrollInActivity(activity.id, scheduleDate);
        setToast(result.message || `Registered for ${activity.title}`);
        await Promise.all([loadActivities(), loadItinerary(scheduleDate)]);
        setSelected(null);
      } catch (e) {
        setJoinErr(e.message || "Could not register");
      } finally {
        setJoining(false);
      }
    }

    function cardClass(a) {
      let cls = "k-card k-act";
      if (a.isEnrolled && a.enrollmentSource === "doctor") cls += " doctor-assigned";
      else if (a.isEnrolled) cls += " enrolled";
      else if (a.hasClash) cls += " clash";
      else if (a.isFull) cls += " full";
      return cls;
    }

    function statusNote(a) {
      if (a.isEnrolled && a.enrollmentSource === "doctor") return "On your programme";
      if (a.isEnrolled) return "Registered";
      if (a.hasClash && a.clashesWith?.length) return `Clashes with ${a.clashesWith[0].title}`;
      if (a.isFull) return "Full";
      return null;
    }

    const sel = selected;
    const activitiesTitle = viewingToday ? "Today's activities" : "Activities";
    const activitiesDateLabel = formatScheduleDate(scheduleDate);

    return (
      <div className="k-page">
        <header className="k-page-head k-page-head-today">
          <div>
            <Pill>Activities</Pill>
            <p className="k-eyebrow k-act-date">{dayLabel} · {activitiesDateLabel}</p>
            <h1 className="k-h1">{activitiesTitle}</h1>
            <p className="k-muted">Open sessions across the sanctuary — tap to view details and join.</p>
          </div>
          <DayNav loading={loading} />
        </header>
        {loading && <p className="k-muted">Loading activities…</p>}
        {error && <p className="k-muted">{error}</p>}
        {!loading && !error && activities.length === 0 && (
          <p className="k-muted">No activities scheduled for {viewingToday ? "today" : "this day"}.</p>
        )}
        <div className="k-act-grid">
          {activities.map((a) => {
            const note = statusNote(a);
            return (
              <button key={a.id} type="button" className={cardClass(a)} onClick={() => { setJoinErr(""); setSelected(a); }}>
                <p className="k-act-time">{a.start} – {a.end}</p>
                <p className="k-act-title">{a.title}</p>
                <p className="k-muted">{a.location}</p>
                {note && <p className="k-act-note">{note}</p>}
                <div className="k-act-foot">
                  <span className="k-tag">{a.level}</span>
                  <span className="k-spots">{a.spots} spots</span>
                </div>
              </button>
            );
          })}
        </div>

        {sel && (
          <div className="k-act-overlay" onClick={() => setSelected(null)} role="presentation">
            <div className="k-act-detail" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <button type="button" className="k-act-close" onClick={() => setSelected(null)} aria-label="Close">×</button>
              <p className="k-act-time">{sel.start} – {sel.end} · {sel.durationMinutes} min</p>
              <h2 className="k-h2">{sel.title}</h2>
              <p className="k-muted">{sel.location}</p>
              <div className="k-act-detail-grid">
                <div><p className="k-eyebrow gold">Instructor{sel.instructors?.length > 1 ? "s" : ""}</p><p>{(sel.instructors || [sel.instructor]).filter(Boolean).join(", ") || "—"}</p></div>
                <div><p className="k-eyebrow gold">Intensity</p><p>{sel.level}</p></div>
                <div><p className="k-eyebrow gold">Spaces</p><p>{sel.spots}{sel.spotsRemaining != null ? ` (${sel.spotsRemaining} left)` : ""}</p></div>
              </div>
              {sel.description && (
                <div className="k-act-desc">
                  <p className="k-eyebrow gold">About</p>
                  <p>{sel.description}</p>
                </div>
              )}
              {sel.hasClash && sel.clashesWith?.length > 0 && (
                <p className="k-act-clash">Overlaps with {sel.clashesWith.map((c) => `${c.title} (${c.start})`).join(", ")}</p>
              )}
              {joinErr && <p className="k-act-join-err">{joinErr}</p>}
              <button
                type="button"
                className="k-primary k-act-join"
                disabled={!sel.canJoin || joining}
                onClick={() => handleJoin(sel)}
              >
                {sel.isEnrolled
                  ? (sel.enrollmentSource === "doctor" ? "On your programme" : "You're registered")
                  : sel.isFull
                    ? "Activity full"
                    : sel.hasClash
                      ? "Schedule clash"
                      : joining
                        ? "Joining…"
                        : "Join activity"}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  function MapView() {
    const nextZone = nextUp ? zoneForLocation(nextUp.location) : null;
    return (
      <div className="k-page">
        <button type="button" className="k-back dark" onClick={() => goSection("today")}>‹ Today</button>
        <PageHead pill="Find your way" title="The sanctuary" sub="42 acres in the Sahyadris, overlooking Mulshi Lake." />
        {nextUp && (
          <div className="k-wayfind">
            Your next session · <strong>{nextUp.title}</strong> at {nextUp.start} — {nextUp.location}, {nextZone}
          </div>
        )}
        <div className="k-mapgrid">
          <div className="k-map">
            <div className="k-map-hill" />
            <div className="k-map-lake">Mulshi Lake</div>
            {MAP_ZONES.map((z) => (
              <span key={z.zone} className={"k-mappin" + (z.zone === nextZone ? " active" : "")} style={{ top: `${z.y}%`, left: `${z.x}%` }}>{z.zone}</span>
            ))}
          </div>
          <div className="k-zonelist">
            {MAP_ZONES.map((z) => (
              <div key={z.zone} className={"k-zone" + (z.zone === nextZone ? " active" : "")}>
                <p className="k-zone-name">{z.zone}</p>
                <p className="k-muted">{z.note}{z.cabins.length ? ` · ${z.cabins.join(" · ")}` : ""}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  function Reflect() {
    if (!profile) return null;
    return (
      <div className="k-page">
        <PageHead pill="Reflect" title="Reflect" sub="Your voice shapes the Atmantan Way." />
        <div className="k-reflect-grid">
          <section>
            {eligible.length > 0 && (
              <div className="k-fb-list">
                {eligible.map((t) => {
                  const rated = treatmentRatings[t.id];
                  return (
                    <button key={t.id} type="button" className={"k-fb-row" + (rated ? " done" : "")} onClick={() => setActiveTreatment(t)}>
                      <div>
                        <p className="k-fb-title">{t.title}</p>
                        <p className="k-muted">{t.start} · {t.instructor}</p>
                      </div>
                      {rated ? <span className="k-fb-badge">{rated.rating}/5</span> : <span className="k-chev">›</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </section>
          <section>
            <div className="k-card">
              {generalDone ? (
                <div className="k-thanks">
                  <Lotus size={30} filled />
                  <p className="k-thanks-title">Thank you, {profile.firstName}.</p>
                  <p className="k-muted">Your reflection has reached our team.</p>
                </div>
              ) : (
                <>
                  <p className="k-q">How has your journey felt so far?</p>
                  <Rating value={generalRating} onChange={setGeneralRating} />
                  <label className="k-ta-label">Anything you'd like us to know</label>
                  <textarea className="k-ta" placeholder="Type here." value={generalText} onChange={(e) => setGeneralText(e.target.value)} />
                  <button
                    type="button"
                    className="k-primary full"
                    disabled={!generalRating}
                    onClick={async () => {
                      try {
                        await submitGeneralFeedback({ rating: generalRating, comment: generalText });
                        setGeneralDone(true);
                        setToast("General feedback shared. Thank you.");
                      } catch (err) {
                        setToast(err.message || "Could not send feedback");
                      }
                    }}
                  >
                    Share reflection
                  </button>
                </>
              )}
            </div>
          </section>
        </div>
      </div>
    );
  }

  function TreatmentFeedback() {
    const t = activeTreatment;
    const existing = treatmentRatings[t.id];
    const [r, setR] = useState(existing?.rating || 0);
    const [c, setC] = useState(existing?.comment || "");
    return (
      <div className="k-page">
        <button type="button" className="k-back dark" onClick={() => setActiveTreatment(null)}>‹ All sessions</button>
        <PageHead pill={`${t.start} · ${t.location}`} title={t.title} sub={`with ${t.instructor}`} />
        <div className="k-card k-detailcard">
          <p className="k-q">How did this session feel?</p>
          <Rating value={r} onChange={setR} big />
          <textarea className="k-ta" placeholder="Share a little about your experience (optional)." value={c} onChange={(e) => setC(e.target.value)} />
          <button
            type="button"
            className="k-primary full"
            disabled={!r}
            onClick={async () => {
              try {
                await submitTreatmentFeedback({
                  bookingKey: t.bookingKey || t.id,
                  rating: r,
                  comment: c,
                });
                setTreatmentRatings({ ...treatmentRatings, [t.id]: { rating: r, comment: c } });
                setActiveTreatment(null);
                setToast(`Feedback on ${t.title} shared. Thank you.`);
              } catch (err) {
                setToast(err.message || "Could not send feedback");
              }
            }}
          >
            {existing ? "Update reflection" : "Share reflection"}
          </button>
        </div>
      </div>
    );
  }

  function Shell() {
    return (
      <div className="k-shell">
        <header className="k-topbar">
          <Logo light />
          <div className="k-topbar-right">
            {profile && (
              <div className="k-guest-chip">
                <span className="k-guest-init">{profile.firstName[0]}</span>
                <div>
                  <p className="k-guest-name">{profile.firstName}</p>
                  <p className="k-guest-meta">{dayLabel}{profile.room ? ` · Room ${profile.room}` : ""}</p>
                </div>
              </div>
            )}
            <button type="button" className="k-finish" onClick={finishSession}>Finish ⟲</button>
          </div>
        </header>
        <main className="k-content">
          {section === "today" && <Today />}
          {section === "activities" && <Activities />}
          {section === "map" && <MapView />}
          {section === "reflect" && (activeTreatment ? <TreatmentFeedback /> : <Reflect />)}
        </main>
        <nav className="k-tabbar">
          {NAV.map((n) => (
            <button key={n.id} type="button" className={"k-tab" + (section === n.id ? " active" : "")} onClick={() => goSection(n.id)}>
              <span className="k-tab-ico"><NavIcon name={n.icon} size={24} /></span>
              <span className="k-tab-label">{n.label}</span>
              {n.id === "reflect" && doneCount < eligible.length && <span className="k-tab-dot" />}
            </button>
          ))}
        </nav>
      </div>
    );
  }

  return (
    <div className="k-stage">
      <div className="k-frame" onPointerDown={bumpIdle}>
        {mode === "attract" && <Attract />}
        {mode === "verify" && <Verify />}
        {mode === "choose" && <ChooseGuest />}
        {mode === "app" && <Shell />}
        {toast && <Toast text={toast} onDone={() => setToast(null)} />}
      </div>
    </div>
  );
}
